import type { TData } from "@/Type/TData";
import type { TErrorType } from "@/Type/TErrorType";
import type { AxiosError, AxiosRequestConfig } from "axios";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import URL from "@/Service/URL";

interface Params<T> {
  data: TData<T>;
  loading: boolean;
  error: TErrorType;
  getData: (endpoint: string, config?: AxiosRequestConfig) => Promise<void>;
  postData: (
    endpoint: string,
    body?: unknown,
    config?: AxiosRequestConfig
  ) => Promise<void>;
  putData: (
    endpoint: string,
    body?: unknown,
    config?: AxiosRequestConfig
  ) => Promise<void>;
  deleteData: (endpoint: string, config?: AxiosRequestConfig) => Promise<void>;
}

const useFunctionInfo = <T>(initialEndpoint: string): Params<T> => {
  const [data, setData] = useState<TData<T>>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<TErrorType>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const cancelRequest = () => abortControllerRef.current?.abort();

  const request = async (
    method: "get" | "post" | "put" | "delete",
    endpoint: string,
    body?: unknown,
    config?: AxiosRequestConfig
  ) => {
    cancelRequest();
    abortControllerRef.current = new AbortController();
    setLoading(true);

    try {
      const response = await axios.request<T>({
        method,
        url: `${URL}${endpoint}`,
        data: body,
        signal: abortControllerRef.current.signal,
        ...config,
      });
      setData(response.data);
      setError(null);
    } catch (err) {
      if (!axios.isCancel(err)) setError(err as AxiosError);
    } finally {
      setLoading(false);
    }
  };

  const getData = useCallback(
    async (endpoint: string, config?: AxiosRequestConfig) => {
      await request("get", endpoint, undefined, config);
    },
    []
  );

  const postData = useCallback(
    async (endpoint: string, body?: unknown, config?: AxiosRequestConfig) => {
      await request("post", endpoint, body, config);
    },
    []
  );

  const putData = useCallback(
    async (endpoint: string, body?: unknown, config?: AxiosRequestConfig) => {
      await request("put", endpoint, body, config);
    },
    []
  );

  const deleteData = useCallback(
    async (endpoint: string, config?: AxiosRequestConfig) => {
      await request("delete", endpoint, undefined, config);
    },
    []
  );

  useEffect(() => {
    if (initialEndpoint) getData(initialEndpoint);
    return () => cancelRequest();
  }, [initialEndpoint]);

  return {
    data,
    loading,
    error,
    getData,
    postData,
    putData,
    deleteData,
  };
};

export default useFunctionInfo;
